import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom-v5-compat";

import Icon from "Atoms/Icons";

import {
  Price,
  ResponsiveTitleBar,
  ResponsivePrimaryAction,
  Button,
  useSubmitButton,
} from "../Atoms";
import { createTabCheckoutSession } from "../Cart/requests";
import { useLoadedCustomer } from "../Customer/queries";
import { useLoadedCustomerTab } from "../CustomerTab/queries";
import { getMaxPaymentAmountFor } from "../CustomerTab/selectors";
import { CustomerPillList, Pill } from "./Pill";
import {
  UnclaimedOrderItem,
  useCreateOrderItemClaim,
} from "./SelectOrderItems";
import { useLoadedBillSplit } from "./queries";
import {
  getClaimedOrderItemsIn,
  getOrderItemsTotal,
  getPaidAtTillAmount,
} from "./selectors";
import { getUnclaimedOrderItems } from "./selectors";
import { getTotalUnassignedTransactionsFor } from "./selectors";

const OwnerReview = () => {
  const { t } = useTranslation();
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: customer } = useLoadedCustomer();
  const { data: billSplit } = useLoadedBillSplit();

  const createOrderItemClaim = useCreateOrderItemClaim(billSplit);
  const claimItem = (orderItemUuid: string) => async () => {
    createOrderItemClaim.mutate(orderItemUuid);
  };

  const unclaimedOrderItems = getUnclaimedOrderItems(billSplit, customerTab);

  const totalUnclaimed = getOrderItemsTotal(unclaimedOrderItems);
  const getClaimedOrderItemsFor = getClaimedOrderItemsIn(
    billSplit,
    customerTab
  );
  const yourClaimedOrderItems = getClaimedOrderItemsFor(customer);
  const yourClaimedOrderItemsTotal = getOrderItemsTotal(yourClaimedOrderItems);
  const getMaxPaymentAmount = getMaxPaymentAmountFor(customerTab);

  const totalUnassignedTransactions = getTotalUnassignedTransactionsFor(
    billSplit,
    customerTab
  );
  const paidAtTillAmount = getPaidAtTillAmount(billSplit, customerTab);
  const yourPaymentTotal =
    yourClaimedOrderItemsTotal + totalUnclaimed - totalUnassignedTransactions;
  const allItemsClaimed = unclaimedOrderItems.length === 0;

  const [submissionState, trackSubmit] = useSubmitButton();
  const startBillSplitCheckoutSession = async () => {
    const checkoutUrl = await createTabCheckoutSession(customerTab.uuid, {
      billSplitCustomerSessionId: billSplit.customerSessions.find(
        (cs) => cs.customerId === customer.uuid
      )?.uuid,
    });
    window.location.assign(checkoutUrl);
    await new Promise((r) => setTimeout(r, 3000));
  };
  return (
    <div className="max-w-screen-sm mx-auto space-y-32">
      <ResponsiveTitleBar>{t("billSplit.finishPaying")} </ResponsiveTitleBar>
      {allItemsClaimed ? (
        <div>
          <h2 className="text-2xl font-bold">
            {" "}
            {t("billSplit.itemsAllAssigned")}{" "}
          </h2>
          <div>{t("billSplit.itemsAllAssignedDescription")}</div>
        </div>
      ) : (
        <>
          <div>
            <h2 className="text-2xl font-bold">
              {" "}
              {t("billSplit.itemsStillInTheBill")}{" "}
            </h2>
            <div>{t("billSplit.asOwnerYoureResponsible")}</div>
            <h3 className="text-xl font-bold">
              {t("billSplit.totalUnclaimed")}{" "}
              <Price showCurrency value={totalUnclaimed} />
            </h3>
          </div>
          <div className="space-y-12">
            <h3 className="text-xl font-bold">
              {t("billSplit.unselected", { count: unclaimedOrderItems.length })}
            </h3>
            <div className="font-bold">
              {t("billSplit.total")}{" "}
              <Price showCurrency value={totalUnclaimed} />
            </div>
            <div className="space-y-8">
              {unclaimedOrderItems?.map((oi) => (
                <UnclaimedOrderItem
                  orderItem={oi}
                  claimItem={claimItem(oi.uuid)}
                />
              ))}
            </div>
          </div>
        </>
      )}
      <h3 className="font-bold text-xl">
        {t("billSplit.paidAtTillAmount")}{" "}
        <Price showCurrency value={paidAtTillAmount} />
      </h3>
      <div className="space-y-12">
        <h3 className="font-bold text-xl">{t("billSplit.customerStatuses")}</h3>

        <CustomerPillList
          billSplit={billSplit}
          customerTab={customerTab}
          currentCustomer={customer}
        />
      </div>
      <div>
        <h3 className="text-xl font-bold">
          {t("billSplit.waitingOnYourFriends")}
        </h3>
        <div>{t("billSplit.waitingOnYourFriendsDescription", {})}</div>
      </div>
      <div className="flex">
        <Link to="../bill">
          <Pill>
            <Icon icon="search" color="venue-primary" />
            {t("billSplit.reviewBill")}
          </Pill>
        </Link>
      </div>
      <ResponsivePrimaryAction>
        {yourPaymentTotal > 0 ? (
          <Button
            primary
            submissionState={submissionState}
            submissionOptions={{
              loadingMessage: t("billSplit.creatingCheckoutSession"),
            }}
            onClick={trackSubmit(startBillSplitCheckoutSession)}
          >
            {t("billSplit.payNow")}
            <Price showCurrency value={yourPaymentTotal} />
          </Button>
        ) : (
          <div className="text-center p-12 font-bold text-lg">
            {t("billSplit.nothingToPay")}
          </div>
        )}
      </ResponsivePrimaryAction>
    </div>
  );
};

export default OwnerReview;
